/** @file buildMenu: turns a subject's registry rows into context-menu items with live chord labels. */
import type { MenuItem } from '../../state/editorTypes';
import { liveChord } from '../liveChord';
import { menuRegistry } from './menuRegistry';
import type { MenuCtxOf, MenuEntry, MenuSubject } from './registryTypes';
import { surfaceContexts } from './surfaceContexts';

/** Removes leading, trailing and doubled separators (left behind by rows whose `when` was false). */
function tidy(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items) {
    if (item.kind === 'separator' && (out.length === 0 || out.at(-1)?.kind === 'separator')) continue;
    out.push(item);
  }
  return out.at(-1)?.kind === 'separator' ? out.slice(0, -1) : out;
}

/** One row as an item: the label, the live chord of its first bound id on this surface, disabled, and its action. */
function toItem<C extends MenuCtxOf[MenuSubject]>(entry: Extract<MenuEntry<C>, { kind: 'item' }>, ctx: C): MenuItem {
  const ids = typeof entry.chords === 'function' ? entry.chords(ctx) : (entry.chords ?? []);
  const chord = liveChord(ids, surfaceContexts(ctx.surface));
  return {
    kind: 'item', label: entry.label(ctx), disabled: entry.disabled?.(ctx) ?? false,
    onSelect: () => { entry.run(ctx); }, ...(chord === undefined ? {} : { chord }),
  };
}

/** The menu of `subject` for `ctx`: rows whose `when` holds, in declaration order, chords read live from the key tables. */
export function buildMenu<S extends MenuSubject>(subject: S, ctx: MenuCtxOf[S]): MenuItem[] {
  const entries: MenuEntry<MenuCtxOf[S]>[] = menuRegistry[subject];
  const items = entries.flatMap((entry): MenuItem[] => {
    if (entry.kind === 'separator') return [{ kind: 'separator' }];
    return entry.when?.(ctx) === false ? [] : [toItem(entry, ctx)];
  });
  return tidy(items);
}
